import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { artistsApi } from '../../repositories/artists';
import Swal from 'sweetalert2';
import CSVPreview from './CSVPreview';
import { ValidationError } from './types';
import { parseAndValidateCSV } from './csvParser';

interface CSVUploadProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CSVUpload: React.FC<CSVUploadProps> = ({ onClose, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [parsing, setParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  const checkDuplicatesBatch = async (validRows: any[]) => {
    setCheckingDuplicates(true);
    const duplicateErrors: ValidationError[] = [];

    try {
      const artistNames = validRows.map(row => row.artistName);
      const spotifyURIs = validRows.map(row => row.spotifyURI);

      const duplicateResponse = await artistsApi.checkDuplicatesBatch({
        artistNames,
        spotifyURIs
      });

      if (duplicateResponse.data.success) {
        const duplicates = duplicateResponse.data.data.duplicates;

        validRows.forEach((row) => {
          const matchingDuplicates = duplicates.filter((dup: any) =>
            dup.artistName.toLowerCase() === row.artistName.toLowerCase() ||
            dup.spotifyURI === row.spotifyURI
          );

          if (matchingDuplicates.length > 0) {
            const duplicateMessages = matchingDuplicates
              .map((dup: any) => `${dup.artistName} (Spotify: ${dup.spotifyURI})`)
              .join(', ');

            duplicateErrors.push({
              // The row's real line in the CSV. `index` is a position in the
              // already-compacted validRows array, so index + 2 pointed at the
              // wrong line whenever an earlier row had failed validation.
              row: row.rowNumber,
              field: 'Duplicate Check',
              message: `Artist already exists in your roster: ${duplicateMessages}`,
              severity: 'error'
            });
          }
        });
      }
    } catch (error) {
      console.error('Duplicate check error:', error);
      duplicateErrors.push({
        row: 0,
        field: 'Duplicate Check',
        message: 'Unable to check for duplicates. Please verify manually.',
        severity: 'warning'
      });
    } finally {
      setCheckingDuplicates(false);
    }

    return duplicateErrors;
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setParsing(true);
      setShowPreview(false);
      setSelectedFile(file);

      try {
        const csvText = await file.text();
        const { validRows, errors } = parseAndValidateCSV(csvText);

        let duplicateErrors: ValidationError[] = [];
        if (validRows.length > 0) {
          duplicateErrors = await checkDuplicatesBatch(validRows);
        }

        /*
         * Duplicate rows stay in the preview data rather than being filtered
         * out here. Removing them meant the preview listed "Row 2: already
         * exists" while the table below was empty and the Errors card read 0 —
         * the counts are derived from the rows, so a row that isn't there
         * cannot be counted. They are excluded at import instead, which is
         * what the "N rows will be skipped" note already promised.
         */
        setCsvData(validRows);
        setValidationErrors([...errors, ...duplicateErrors]);
        setShowPreview(true);
        setSelectedFile(file);

      } catch (err: any) {
        console.error('CSV parsing error:', err);
        Swal.fire({
          title: 'CSV Parsing Error',
          text: 'Failed to parse CSV file. Please check the format.',
          icon: 'error'
        });
      } finally {
        setParsing(false);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB limit
  });

  const handleImport = async () => {
    try {
      setUploading(true);

      // Rows carrying an error (missing fields, duplicates) are excluded here —
      // csvData now holds every parsed row so the preview can show them.
      const importableRows = (csvData || []).filter(
        (row) =>
          !validationErrors.some(
            (error) => error.row === row.rowNumber && error.severity === 'error'
          )
      );

      if (importableRows.length === 0) {
        Swal.fire({
          title: 'No Data to Import',
          text: 'No valid CSV data available for import.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      Swal.fire({
        title: 'Importing Artists...',
        html: `
          <div class="text-center">
            <div class="w-12 h-12 border-b-2 border-primary-700 rounded-full animate-spin mx-auto mb-4"></div>
            <p class="text-sm text-neutral-600" id="import-progress">Uploading file...</p>
          </div>
        `,
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const progressText = document.getElementById('import-progress');
      if (progressText) progressText.textContent = 'Importing artists...';

      const response = await artistsApi.importCSV(importableRows);
      const result = response.data;

      if (result.success) {
        Swal.close();

        Swal.fire({
          title: 'Import Successful!',
          html: `
            <div class="text-center">
              <p class="text-sm text-neutral-600">
                Successfully imported <strong>${result.data?.totalSuccess || importableRows.length}</strong> artists
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'Done',
        });

        onSuccess();
      } else {
        throw new Error(result.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Import error:', error);

      Swal.close();

      Swal.fire({
        title: 'Import Failed',
        html: `
          <div class="text-center">
            <p class="text-sm text-neutral-600">
              ${error.response?.data?.message || 'Failed to import artists. Please try again.'}
            </p>
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'Try Again',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full overflow-y-auto bg-neutral-600 bg-opacity-50">
      <div className="relative w-11/12 max-w-6xl p-5 mx-auto overflow-y-auto bg-white border rounded-md shadow-lg top-10" style={{ maxHeight: '60vh' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-black">
            {uploading ? 'Importing Artists...' :
              parsing ? 'Parsing CSV...' :
                checkingDuplicates ? 'Checking for Duplicates...' :
                  showPreview ? 'CSV Preview' : 'Upload CSV File'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
            disabled={uploading || parsing || checkingDuplicates}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {(parsing || checkingDuplicates) && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 border-b-2 border-primary-700 rounded-full animate-spin"></div>
            <p className="mt-4 text-sm text-neutral-600">
              {parsing ? 'Parsing CSV file...' : 'Checking for duplicates...'}
            </p>
          </div>
        )}

        {!showPreview && !parsing && !checkingDuplicates && (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-neutral-300 hover:border-neutral-400'
                }`}
            >
              <input  {...getInputProps()} />
              <svg
                className="w-12 h-12 mx-auto text-neutral-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <div className="mt-4">
                <p className="text-lg font-medium text-black">
                  {isDragActive ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
                </p>
                <p className="mt-2 text-sm text-neutral-500">
                  or click to browse files (max 10MB)
                </p>
                <p className="mt-1 text-xs text-neutral-400">
                  Download the template above to see the required format
                </p>
              </div>
            </div>
          </div>
        )}

        {showPreview && !parsing && !checkingDuplicates && (
          <CSVPreview
            data={csvData}
            errors={validationErrors}
            onImport={handleImport}
            onBack={() => {
              setShowPreview(false);
              setSelectedFile(null);
              setCsvData([]);
              setValidationErrors([]);
            }}
            uploading={uploading}
          />
        )}
      </div>
    </div>
  );
};

export default CSVUpload;
